import React, { useCallback, useEffect, useState } from "react";
import { HeartHandshake, Check, AlertTriangle, Repeat2, Megaphone } from "lucide-react";
import { SwapOfferDialog, OutgoingSwapStatus, IncomingSwaps, OpenShiftBoard } from "@/components/VolunteerSwaps";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";
import { prettyYmd } from "@/components/ConflictDialog";
import { FillBar, blockTime } from "@/pages/coach/Volunteers";
import { PastShiftsCard } from "@/components/ChildVolunteerShifts";

function MemberBlock({ b, onSignup, onCancel, busy, swap, onOffer, onSwapDone }) {
  const mine = !!b.my_signup_id;
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 ${mine ? "border-teal-200 bg-teal-50/60" : "border-slate-100 bg-slate-50/60"}`} data-testid={`mvol-block-${b.id}`}>
      <div>
        <p className="text-sm font-semibold text-slate-800">{blockTime(b)}</p>
        <FillBar filled={b.filled} spots={b.spots} testId={`mvol-block-fill-${b.id}`} />
      </div>
      {mine ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1 rounded-full bg-teal-600 px-2.5 py-0.5 text-xs font-semibold text-white" data-testid={`mvol-mine-${b.id}`}><Check className="h-3.5 w-3.5" /> Signed up</span>
          {swap ? <OutgoingSwapStatus swap={swap} onDone={onSwapDone} /> : !b.started && (<>
            <Button size="sm" variant="outline" className="h-7 rounded-full text-teal-700" onClick={() => onOffer(b)} data-testid={`mvol-offer-swap-${b.id}`}><Repeat2 className="mr-1 h-3.5 w-3.5" /> Offer swap</Button>
            <Button size="sm" variant="outline" className="h-7 rounded-full text-teal-700" onClick={() => onOffer(b, true)} data-testid={`mvol-post-board-${b.id}`}><Megaphone className="mr-1 h-3.5 w-3.5" /> Post to board</Button>
          </>)}
          <Button size="sm" variant="ghost" className="h-7 rounded-full text-slate-500" onClick={() => onCancel(b)} data-testid={`mvol-cancel-${b.id}`}>Cancel</Button>
        </div>
      ) : b.started ? (
        <span className="text-xs text-slate-400" data-testid={`mvol-started-${b.id}`}>Started</span>
      ) : b.open <= 0 ? (
        <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-500" data-testid={`mvol-full-${b.id}`}>Full</span>
      ) : (
        <Button size="sm" className="h-8 rounded-full bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={() => onSignup(b)} data-testid={`mvol-signup-${b.id}`}>Sign up</Button>
      )}
    </div>
  );
}

function BlockedDialog({ open, onClose, hours }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="mvol-cancel-blocked-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><AlertTriangle className="h-5 w-5 text-amber-500" /> Can't cancel in the app</DialogTitle>
          <DialogDescription>Sign-ups can't be cancelled within {hours} hours of the time block. Please notify your coach directly if you need to cancel — or use “Offer swap” or “Post to board” to hand your shift off (your coach approves or reposts it).</DialogDescription>
        </DialogHeader>
        <DialogFooter><Button className="rounded-full" onClick={onClose} data-testid="mvol-cancel-blocked-ok">OK</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MemberVolunteers() {
  const [items, setItems] = useState(null);
  const [hours, setHours] = useState(48);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [swaps, setSwaps] = useState({ outgoing: [], incoming: [] });
  const [offering, setOffering] = useState(null);
  const [board, setBoard] = useState([]);
  const [history, setHistory] = useState({ past: [], past_hours: 0 });

  const load = useCallback(() => {
    api.get("/volunteers/swaps/my").then(({ data }) => setSwaps(data)).catch(() => {});
    api.get("/volunteers/board").then(({ data }) => setBoard(data.items || [])).catch(() => {});
    api.get("/volunteers/my/shifts").then(({ data }) => setHistory(data)).catch(() => {});
    return api.get("/volunteers/available").then(({ data }) => { setItems(data.items || []); setHours(data.cancel_hours || 48); }).catch(() => setItems([]));
  }, []);
  const activeOut = Object.fromEntries(swaps.outgoing.filter((s) => ["offered", "posted", "accepted"].includes(s.status)).map((s) => [s.signup_id, s]));
  useEffect(() => { load(); }, [load]);

  const signup = async (b) => {
    setBusy(true);
    try { await api.post(`/volunteers/blocks/${b.id}/signup`); toast.success("You're signed up — thank you!"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); load(); }
  };
  const askCancel = (b) => (b.can_cancel ? setCancelling(b) : setBlocked(true));
  const doCancel = async () => {
    const b = cancelling; setCancelling(null);
    try { await api.delete(`/volunteers/my/${b.my_signup_id}`); toast.success("Sign-up cancelled"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { load(); }
  };

  if (items === null) return null;
  const mine = items.flatMap((s) => s.events.flatMap((e) => e.blocks.filter((b) => b.my_signup_id).map((b) => ({ s, e, b }))));
  return (
    <div data-testid="member-volunteers-page">
      <p className="mb-4 text-sm text-slate-500">Pick an open time block to volunteer. You can cancel up to {hours} hours before your block starts.</p>
      <IncomingSwaps items={swaps.incoming} onDone={load} />
      <OpenShiftBoard items={board} onDone={load} />
      {mine.length > 0 && (
        <Card className="mb-4 border-teal-200 bg-teal-50/40 p-4 shadow-sm" data-testid="mvol-my-signups">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-teal-700">My sign-ups</p>
          <ul className="space-y-1 text-sm text-slate-700">
            {mine.map(({ s, e, b }) => <li key={b.id} data-testid={`mvol-my-${b.id}`}><span className="font-semibold">{s.title}</span> · {prettyYmd(e.event_date)} · {blockTime(b)}</li>)}
          </ul>
        </Card>
      )}
      {items.length === 0 ? (
        <EmptyState icon={HeartHandshake} title="No volunteer opportunities right now" description="When your coach posts a sign-up sheet, it'll show up here." testId="mvol-empty" />
      ) : (
        <div className="grid max-w-3xl gap-4">
          {items.map((s) => (
            <Card key={s.id} className="border-slate-200 p-5 shadow-sm" data-testid={`mvol-sheet-${s.id}`}>
              <p className="font-heading text-lg font-semibold text-slate-900">{s.title}</p>
              {s.details && <p className="text-sm text-slate-500">{s.details}</p>}
              <div className="mt-4 space-y-4">
                {s.events.map((ev) => (
                  <div key={ev.id}>
                    <p className="text-sm font-bold text-slate-800">{prettyYmd(ev.event_date)}</p>
                    <p className="mb-2 text-sm text-slate-500">{ev.description}</p>
                    <div className="space-y-2">{ev.blocks.map((b) => <MemberBlock key={b.id} b={b} busy={busy} onSignup={signup} onCancel={askCancel}
                      swap={activeOut[b.my_signup_id]} onOffer={(blk, toBoard) => setOffering({ text: `${s.title}: ${prettyYmd(ev.event_date)}, ${blockTime(blk)}`, signupId: blk.my_signup_id, board: !!toBoard })} onSwapDone={load} />)}</div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
      <div className="mt-4 max-w-3xl"><PastShiftsCard past={history.past} hours={history.past_hours} /></div>
      <ConfirmDialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)} title="Cancel your sign-up?" confirmText="Cancel sign-up" destructive testId="mvol-cancel-dialog"
        description={cancelling ? `You'll be removed from ${blockTime(cancelling)} and your coach will be notified.` : ""} onConfirm={doCancel} />
      <BlockedDialog open={blocked} onClose={() => setBlocked(false)} hours={hours} />
      {offering && <SwapOfferDialog block={offering.text} signupId={offering.signupId} board={offering.board} onClose={() => setOffering(null)} onDone={load} />}
    </div>
  );
}
